import React, { useState, useEffect } from "react";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { LogoutModal } from "../modals/LogoutModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import apiClient from "../../api/apiClient";
import { all_routes } from "../../Router/all_routes";
import {
  setbearer_token,
  setuser_data,
  setcurrent_store,
  setuserpermission_data,
  setcurrent_cart,
  setrolesandpermission_data,
} from "../../core/redux/action";
import { useTheme } from "@/components/theme-provider";
import { cn } from "@/lib/utils";
import useTabCloseLogout from "../../core/utils/useTabCloseLogout";

export default function AppShell({ children }) {
  const { theme } = useTheme();
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  // Auto-logout when ALL tabs of this app are closed
  useTabCloseLogout();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const currentStore = useSelector((state) => state.current_store);

  useEffect(() => {
    if (currentStore?.name) {
      document.title = `${currentStore.name} - ERF Dashboard`;
    } else {
      document.title = "Easy Retail Flow - ERF";
    }
  }, [currentStore]);

  useEffect(() => {
    // Initialize draftData in localStorage if it doesn't exist
    // This is used by legacy components (like Add Product) to save progress
    if (!localStorage.getItem("draftData")) {
      localStorage.setItem("draftData", JSON.stringify({}));
    }
  }, []);

  const logoutMutation = useMutation({
    mutationFn: () => apiClient.post("/logout", {}),
    onSettled: () => {
      dispatch(setuser_data(null));
      dispatch(setbearer_token(null));
      dispatch(setcurrent_store(null));
      dispatch(setuserpermission_data([]));
      dispatch(setcurrent_cart([]));
      dispatch(setrolesandpermission_data([]));
      queryClient.clear();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = all_routes.signin;
    },
  });

  const handleLogout = () => {
    setShowLogoutModal(true);
  };

  return (
    <div className="flex min-h-screen bg-background font-sans selection:bg-primary/10">
      {/* Desktop Sidebar */}
      <div
        className={cn(
          "hidden border-r xl:block xl:w-80 2xl:w-84 min-[2160px]:w-[420px] fixed inset-y-0 z-50 transition-all duration-300 shadow-sm opacity-100",
          theme === "dark" ? "bg-slate-950" : "bg-white",
        )}
      >
        <Sidebar className="h-full" onLogout={handleLogout} />
      </div>

      <div className="flex flex-col flex-1 min-w-0 xl:pl-80 2xl:pl-84 min-[2160px]:pl-[420px] transition-all duration-300">
        <div className="flex flex-col flex-1 w-full max-w-[2560px]">
          <Header onLogout={handleLogout} />
          <main className="flex-1 w-full">{children}</main>
        </div>
      </div>

      <LogoutModal
        open={showLogoutModal}
        onConfirm={() => logoutMutation.mutate()}
        onCancel={() => setShowLogoutModal(false)}
        isLoading={logoutMutation.isPending}
      />
    </div>
  );
}
